import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import dayjs from 'dayjs';

export default async function CustomerLoanDetailsExcel(
    data,
    loanNumber,
    generatedAt = new Date()
) {
    if (!data || !data.customerNumber) {
        return;
    }

    const workbook = new ExcelJS.Workbook();
    const sheet = workbook.addWorksheet('Customer Loan Details');

    const totalColumns = 10;

    // === TITLE SECTION ===
    sheet.mergeCells(1, 1, 1, totalColumns);
    const titleCell = sheet.getCell('A1');
    titleCell.value = `Customer Loan Details Report - ${loanNumber}`;
    titleCell.font = { bold: true, size: 18, color: { argb: '000080' } };
    titleCell.alignment = { horizontal: 'center', vertical: 'middle' };
    titleCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'DDEBF7' } };
    sheet.getRow(1).height = 50;

    sheet.mergeCells(2, 1, 2, totalColumns);
    const subTitle = sheet.getCell('A2');
    subTitle.value = 'All Loans (Active, Non-Performing & Closed)';
    subTitle.font = { bold: true, size: 14 };
    subTitle.alignment = { horizontal: 'center' };
    sheet.getRow(2).height = 35;

    sheet.mergeCells(3, 1, 3, totalColumns);
    const generatedCell = sheet.getCell('A3');
    generatedCell.value = `Generated on: ${dayjs(generatedAt).format('YYYY-MM-DD HH:mm')}`;
    generatedCell.font = { italic: true, size: 11 };
    generatedCell.alignment = { horizontal: 'center' };
    sheet.getRow(3).height = 25;

    sheet.addRow([]);

    // === CUSTOMER INFO SECTION ===
    const customerInfoStart = 5;
    const customerInfo = [
        ['Customer Number', data.customerNumber || '-'],
        ['Customer Name', data.customerName || '-'],
        ['NIC ID', data.nicId || '-'],
        ['Contact Number', data.contactNumber || '-'],
        ['Address', data.address || '-'],
        ['Customer Type', data.customerType || '-'],
        ['Branch Name', data.branchName || '-'],
    ];

    customerInfo.forEach(([label, value], idx) => {
        const rowNum = customerInfoStart + idx;
        const labelCell = sheet.getCell(`A${rowNum}`);
        const valueCell = sheet.getCell(`B${rowNum}`);

        labelCell.value = label;
        valueCell.value = value;
        labelCell.font = { bold: true };
        labelCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'F2F2F2' } };

        const borderStyle = {
            top: { style: 'thin' },
            bottom: { style: 'thin' },
            left: { style: 'thin' },
            right: { style: 'thin' }
        };
        labelCell.border = borderStyle;
        valueCell.border = borderStyle;
    });

    sheet.addRow([]);
    sheet.addRow([]);

    // === LOANS TABLE HEADER ===
    const headers = [
        'Loan No.',
        'Loan A/C No',
        'Loan Type',
        'Loan Amount',
        'Disbursed Amount',
        'Total Recovered',
        'Outstanding Amount',
        'Interest Rate (%)',
        'Loan Status',
        'Disbursement Date'
    ];

    const headerRow = sheet.addRow(headers);
    headerRow.eachCell((cell) => {
        cell.font = { bold: true, color: { argb: 'FFFFFF' } };
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: '4472C4' } };
        cell.alignment = { horizontal: 'center', vertical: 'middle' };
        cell.border = {
            top: { style: 'thin' },
            bottom: { style: 'thin' },
            left: { style: 'thin' },
            right: { style: 'thin' }
        };
    });

    // === LOANS DATA ===
    let zebraIndex = 0;

    // Sort: Active → Non-Performing / NPA → Closed
    const statusOrder = (s) => {
        const v = (s || '').toLowerCase();
        if (v === 'active') return 0;
        if (v === 'closed') return 2;
        return 1; // non-performing, npa, etc.
    };

    const sortedLoans = [...(data.loans || [])].sort(
        (a, b) => statusOrder(a.loanStatus) - statusOrder(b.loanStatus)
    );

    if (sortedLoans.length > 0) {
        sortedLoans.forEach((loan) => {
            const status = loan.loanStatus?.toLowerCase();
            let outstanding = 0;
            if (status !== "closed") {
                const calculated = (loan.disbursedAmount || 0) - (loan.totalRecovered || 0);
                outstanding = Math.max(0, calculated);
            }

            const row = sheet.addRow([
                loan.loanNumber || '-',
                loan.loanAccountNumber || '-',
                loan.loanTypeName || '-',
                loan.loanAmount || 0,
                loan.disbursedAmount || 0,
                loan.totalRecovered || 0,
                outstanding,
                loan.interestRate || '-',
                loan.loanStatus || '-',
                loan.disbursementDate ? dayjs(loan.disbursementDate).format('YYYY-MM-DD') : '-'
            ]);

            const zebraColor = zebraIndex % 2 === 0 ? 'FFFFFF' : 'F2F2F2';
            row.eachCell((cell, colNum) => {
                cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: zebraColor } };
                cell.border = {
                    top: { style: 'thin' },
                    bottom: { style: 'thin' },
                    left: { style: 'thin' },
                    right: { style: 'thin' }
                };

                // Number formatting (Loan Amt, Disbursed, Recovered, Outstanding, Interest)
                if ([4, 5, 6, 7, 8].includes(colNum)) {
                    cell.numFmt = colNum === 8 ? '0.00' : '#,##0.00';
                }
            });

            zebraIndex++;
        });
    } else {
        const noDataRow = sheet.addRow(['No loan records found for this customer']);
        sheet.mergeCells(noDataRow.number, 1, noDataRow.number, totalColumns);
        noDataRow.getCell(1).alignment = { horizontal: 'center' };
        noDataRow.getCell(1).font = { italic: true, color: { argb: '808080' } };
    }

    // === COLUMN WIDTHS ===
    sheet.columns = [
        { width: 22 },  // Loan No.
        { width: 30 },  // Loan A/C No
        { width: 45 },  // Loan Type (Also helps with Customer Info values like Address)
        { width: 25 },  // Loan Amount
        { width: 25 },  // Disbursed Amount
        { width: 22 },  // Total Recovered
        { width: 25 },  // Outstanding Amount
        { width: 20 },  // Interest Rate
        { width: 22 },  // Status
        { width: 22 }   // Date
    ];

    sheet.views = [{ state: 'frozen', ySplit: 3 }];

    // === EXPORT ===
    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(new Blob([buffer]), `Customer_Loan_Details_${loanNumber}_${dayjs(generatedAt).format('YYYYMMDD_HHmm')}.xlsx`);
}
